import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, AlertTriangle } from 'lucide-react';
import { formatDay } from '@/lib/tenantLeaseDetails';
import { projectStatusLabel, projectStatusColor, showsProjectStatus } from '@/lib/projectStatus';
import StatusPicker from '../project/StatusPicker';

/**
 * One project as a tile. Its list-view counterpart is ProjectListRow, and the
 * two share their status labels and their date formatting so that the same
 * project never reads differently depending on which view is on.
 *
 * The date goes through formatDay rather than new Date(): the API returns a
 * plain YYYY-MM-DD, which new Date() reads as midnight UTC and renders as the
 * day before for everyone at Westwood.
 *
 * tenant_in_default tints the tile light orange - set from the Default Status
 * card on the Lease Details tab. The tint is a courtesy read of the same field
 * the tab writes; it carries no access rule of its own.
 *
 * `changeStatus` swaps the badge for the StatusPicker, which falls back to the
 * same badge for anyone who cannot edit the project. The pages turn it on; it
 * is a prop rather than the default because the picker asks who is signed in,
 * and a tile rendered on its own has nobody to ask.
 */
export default function ProjectCard({ project, percentComplete, changeStatus = false }) {
  const inDefault = !!project.tenant_in_default;
  return (
    <Link to={`/ProjectDetail?id=${project.id}`}>
      <Card
        className={`p-5 hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer group ${
          inDefault ? 'bg-orange-50 border-orange-300' : ''
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground">{project.project_number}</p>
            <h3 className="font-semibold text-sm mt-0.5 truncate group-hover:text-accent transition-colors">{project.name}</h3>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {changeStatus ? (
              <StatusPicker project={project} />
            ) : showsProjectStatus(project) && (
              <Badge className={`text-[10px] ${projectStatusColor(project.status)}`}>
                {projectStatusLabel(project.status)}
              </Badge>
            )}
            {inDefault && (
              <Badge
                className="text-[10px] bg-orange-200 text-orange-800 gap-1"
                title={project.tenant_default_reason || 'In default of lease commitments'}
              >
                <AlertTriangle className="w-2.5 h-2.5" /> In Default
              </Badge>
            )}
          </div>
        </div>

        {project.address && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{project.address}</span>
          </div>
        )}

        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
          {project.start_date && (
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>{formatDay(project.start_date)}</span>
            </div>
          )}
          {project.type && (
            <span className="capitalize">{project.type.replace(/_/g, ' ')}</span>
          )}
        </div>

        {/* Progress bar */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-muted-foreground">Progress</span>
            <span className="text-[10px] font-semibold">{percentComplete}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-accent rounded-full transition-all duration-500"
              style={{ width: `${percentComplete}%` }}
            />
          </div>
        </div>
      </Card>
    </Link>
  );
}
